import { Inject, Injectable, NgZone } from '@angular/core';
import { Page } from './page';
import { DirectionalProactiveQueryResolver } from './proactive-query-resolver';
import { ArrayImmutablePaginatedCache } from './immutable-paginated-cache';
import type { Observable } from 'rxjs';
import type { BackendError } from '../../domain/error.domain';
import type { CachingDataSourceQuery } from './caching-data-source-query';
import type { ProactiveQueryResolver } from './proactive-query-resolver';
import type { ImmutablePaginatedCache } from './immutable-paginated-cache';

export interface ResultSetMetaInfo {
  offset: number;
  totalRecords: number;
  resultSetLimit?: number;
  totalSizeExceedLimit?: boolean;
}

export interface CachingDataSourceResult<T> {
  records: Array<T>;
  resultSetMetaInfo: ResultSetMetaInfo;
}

export interface FetchedDataSource<T, Q extends CachingDataSourceQuery, E = BackendError> {
  fetchData(query: Q): Observable<unknown>;

  extractResult(result: unknown): CachingDataSourceResult<T>;

  handleError(error: E): void;
}

@Injectable()
export class CachingDataSourceFactory {

  constructor(@Inject(NgZone) private ngZone: NgZone) {
  }

  createDataSource<T, Q extends CachingDataSourceQuery, E>(dataFetchSource: FetchedDataSource<T, Q, E>,
                                                           proactiveQueryResolver?: ProactiveQueryResolver<Q>,
                                                           cache?: ImmutablePaginatedCache<T, Q>): CachingDataSourceImpl<T, Q, E> {
    return new CachingDataSourceImpl<T, Q, E>(dataFetchSource, proactiveQueryResolver, this.ngZone, cache);
  }
}

export interface CachingDataSource<T, Q extends CachingDataSourceQuery> {

  isInitialized(): boolean;

  getPageSize(): number;

  setPageSize(pageSize: number): void;

  getDataSourceDate(): Date;

  getRows(): Array<T>;

  getQuery(): Q;

  getTotalRecords(): number | undefined;

  getPageNumber(): number;

  updatePage(query: Q, refresh?: boolean): void;

  getResultSetMetaInfo(): ResultSetMetaInfo | undefined;
}

/**
 * The caching datasource
 *
 * This is the datasource supporting:
 * <ol>
 * <li>Caching data</li>
 * <li>Automatic cache refresh if new search creates different result, i.e. changing page number in search query
 * does not refresh cache {@link CachingDataSourceQuery#shouldRefreshCache}. Changing essential query criteria, like</li>
 * <li>Proactively fetching the source data before reaching the end of the currently loaded data.</li>
 * <li>Integration with Angular's {@link NgZone)</li>
 * </ol>
 */
class CachingDataSourceImpl<T, Q extends CachingDataSourceQuery, E> implements CachingDataSource<T, Q> {

  private dataSourceDate: Date;
  private query: Q;
  private pageSize: number;
  private resultSetMetaInfo: ResultSetMetaInfo;

  constructor(private dataFetchSource: FetchedDataSource<T, Q, E>,
              private proactiveQueryResolver: ProactiveQueryResolver<Q> | undefined = new DirectionalProactiveQueryResolver<Q>(),
              private ngZone: NgZone,
              private cache: ImmutablePaginatedCache<T, Q> = new ArrayImmutablePaginatedCache<T, Q>()) {
    this.pageSize = new Page().getPageSize();
  }

  isInitialized(): boolean {
    return this.query !== undefined && this.query !== null;
  }

  getPageSize(): number {
    return this.pageSize;
  }

  setPageSize(pageSize: number): void {
    this.pageSize = pageSize;
  }

  getDataSourceDate(): Date {
    return this.dataSourceDate;
  }

  getRows(): Array<T> {
    return this.cache.getRows();
  }

  getQuery(): Q {
    return this.query;
  }

  getTotalRecords(): number | undefined {
    return this.isInitialized() ? this.query.getPage().getTotalRecords() : 0;
  }

  getPageNumber(): number {
    return this.isInitialized() ? this.query.getPage().getPageNumber() : 0;
  }

  getResultSetMetaInfo(): ResultSetMetaInfo | undefined {
    return this.isInitialized() ? this.resultSetMetaInfo : undefined;
  }

  updatePage(query: Q, refresh?: boolean): void {
    if (!(refresh ?? false) && this.isPageCached(query)) {
      this.proactiveDataFetch(query);
      this.getQuery().getPage().setPageNumber(query.getPage().getPageNumber());
    } else {
      this.executeFetch(query, false, refresh);
    }
  }

  private proactiveDataFetch(query: Q): void {
    const proactiveQuery = this.proactiveQueryResolver?.estimateQueryInAdvance(this.getQuery(), query, this.cache);
    if (proactiveQuery !== undefined && proactiveQuery !== null) {
      this.executeFetch(proactiveQuery, true);
    }
  }

  private executeFetch(query: Q, proactiveAdd?: boolean, refresh?: boolean): void {
    this.cache.markRequestedPages(query);
    this.dataFetchSource.fetchData(query).subscribe({
      next: (result: unknown): void => {
        if ((refresh ?? false) || !this.isPageCached(query)) {
          const extractedResult = this.dataFetchSource.extractResult(result);
          this.addRows(query, extractedResult, proactiveAdd, refresh);
        }
      },
      error: (error: E): void => {
        this.dataFetchSource.handleError(error);
      }
    });
  }

  private addRows(query: Q, result: CachingDataSourceResult<T>, proactiveAdd?: boolean, refresh?: boolean): void {
    if ((refresh ?? false) || query.shouldRefreshCache(this.query)) {
      this.reset(query, result, refresh);
    } else {
      this.ngZone.run((): void => {
        this.insertRows(query, result, proactiveAdd);
      });
    }
  }

  private reset(query: Q, result: CachingDataSourceResult<T>, refresh?: boolean): void {
    if (!(refresh ?? false)) {
      this.dataSourceDate = new Date();
    }
    query.getPage().updateTotalRecords(this.getTotalRecord(result.resultSetMetaInfo));
    this.query = query;
    this.resultSetMetaInfo = result.resultSetMetaInfo;
    this.cache.reset(query, result.resultSetMetaInfo.offset, this.getTotalRecord(result.resultSetMetaInfo), result.records);
  }

  private insertRows(query: Q, result: CachingDataSourceResult<T>, proactiveAdd?: boolean): void {
    query.getPage().updateTotalRecords(this.getTotalRecord(result.resultSetMetaInfo));
    if (!(proactiveAdd ?? false)) {
      this.query = query;
    }
    this.resultSetMetaInfo = result.resultSetMetaInfo;
    this.cache.addRows(query, result.resultSetMetaInfo.offset, this.getTotalRecord(result.resultSetMetaInfo), result.records);
  }

  private isPageCached(query: Q): boolean {
    return this.isInitialized() && !query.shouldRefreshCache(this.query) && this.cache.isPageCached(query.getPage().getPageNumber());
  }

  private getTotalRecord(metaInfo: ResultSetMetaInfo): number | undefined {
    return ((metaInfo.totalSizeExceedLimit ?? false) ? metaInfo.resultSetLimit : metaInfo.totalRecords);
  }
}

type PropDescriptorFunc = (_target: unknown, _propertyKey: string, propertyDescriptor: PropertyDescriptor) => PropertyDescriptor;

export const PreventMultiCall = (): PropDescriptorFunc => (_target: unknown,
                                                           _propertyKey: string,
                                                           propertyDescriptor: PropertyDescriptor): PropertyDescriptor => {
  const method = propertyDescriptor.value;
  let lastArgs: string;
  propertyDescriptor.value = function(...args: Array<unknown>): void {
    const argsString = JSON.stringify(args);
    if (argsString !== lastArgs) {
      lastArgs = argsString;
      return method.apply(this, args);
    }
  };
  return propertyDescriptor;
};
